import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole, hasAdminManagerRole } from '../../../utils/roles';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminAPI } from '../../../services/api';
import { FiUser, FiUserPlus, FiEdit, FiX, FiCheck, FiToggleRight, FiToggleLeft, FiStar, FiPackage, FiClipboard, FiTruck, FiActivity, FiCalendar, FiEye } from 'react-icons/fi';
import PermissionCheckboxList from '../../../components/PermissionCheckboxList';
import PermissionButton from '../../../components/PermissionButton';
import { PermissionGate } from '../../../contexts/PermissionContext';
import CustomerFeesManager from '../../../components/CustomerFeesManager';
import StorageAgreementPanel from '../../../components/StorageAgreementPanel';
import CompanyWarehousesTab from '../../../components/CompanyWarehousesTab';
import { format } from 'date-fns';
import { formatStatus } from '../../../utils/formatters';
import ActivityTimeline from '../../../components/ActivityTimeline';
import ContractsTab from '../../../components/ContractsTab';

const defaultServiceCapabilities = {
  incoming: true,
  inbound_project: true,
  ship: true,
  crossdock: true,
  pick_pack: true,
  kit_build: true,
  require_request_approval: false
};
const defaultReceivingPreferences = {
  require_serial_numbers_on_receive: false,
  require_mac_addresses_on_receive: false,
  relabel_on_receive: false
};
const emptyAddress = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: ''
};
const emptyBillingInfo = {
  billingContactName: '',
  billingContactEmail: '',
  billingContactPhone: ''
};
const emptyBillingAddress = {
  addressId: '',
  attention: '',
  street: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  phone: '',
  fax: ''
};

const serviceCapabilityOptions = [
  ['incoming', 'Incoming Inventory'],
  ['inbound_project', 'Inbound Manifest'],
  ['ship', 'Ship Products'],
  ['crossdock', 'Cross-Dock'],
  ['pick_pack', 'Pick & Pack'],
  ['kit_build', 'Kit Building']
];

const getCustomerUserStatus = (user) => {
  if (user?.status) return user.status;
  if (user?.is_active === false) return 'inactive';
  if (user?.password_reset_token && user?.password_reset_expires) {
    return new Date(user.password_reset_expires) > new Date() ? 'invited' : 'expired';
  }
  return 'accepted';
};

const CUSTOMER_USER_STATUS_BADGES = {
  invited: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  expired: 'bg-amber-100 text-amber-800',
  inactive: 'bg-red-100 text-red-800'
};

const CUSTOMER_USER_STATUS_LABELS = {
  invited: 'Invited',
  accepted: 'Accepted',
  expired: 'Expired',
  inactive: 'Inactive'
};

const parseMaybeJsonObject = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === 'string') {
    try {
      return {
        ...fallback,
        ...JSON.parse(value)
      };
    } catch (e) {
      return fallback;
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return {
      ...fallback,
      ...value
    };
  }

  return fallback;
};

const buildFormDataFromCustomer = (customer) => {
  const addressObj = parseMaybeJsonObject(customer?.address, emptyAddress);
  const billingInfoObj = parseMaybeJsonObject(customer?.billingInfo, emptyBillingInfo);
  const billingAddressOverride = parseMaybeJsonObject(customer?.billingAddressOverride, emptyBillingAddress);
  const zohoBillingAddress = parseMaybeJsonObject(customer?.zohoBillingAddress, emptyBillingAddress);

  let primaryContactName = '';
  if (customer?.Users && Array.isArray(customer.Users)) {
    const primaryUser = customer.Users.find(user => user.is_primary === true);
    if (primaryUser) {
      primaryContactName = primaryUser.name;
    } else if (customer.Users.length > 0) {
      primaryContactName = customer.Users[0].name;
    }
  }

  return {
    companyName: customer?.name || '',
    contactName: primaryContactName,
    email: customer?.email || '',
    phone: customer?.phone || '',
    address: addressObj,
    isActive: customer?.is_active !== undefined ? customer.is_active : true,
    notes: customer?.notes || '',
    billingInfo: billingInfoObj,
    zohoCustomerId: customer?.zohoCustomerId || '',
    zohoBillingAddress,
    billingAddressOverride,
    receivingPreferences: {
      ...defaultReceivingPreferences,
      ...(customer?.receivingPreferences || customer?.receiving_preferences || {})
    },
    serviceCapabilities: {
      ...defaultServiceCapabilities,
      ...(customer?.serviceCapabilities || customer?.service_capabilities || {})
    }
  };
};

const CustomerDetail = () => {
  // Backend role gates (RF15): delete is admin/manager; the invoice
  // billing-address override is billing-role only.
  const { user: authUser } = useAuth();
  const canMutateBilling = hasBillingRole(authUser);
  const canDeleteCustomer = hasAdminManagerRole(authUser);
  const navigate = useNavigate();
  const { id } = useParams(); // Get customer ID from URL
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [customerRequests, setCustomerRequests] = useState([]);
  const [customerInventory, setCustomerInventory] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'regular_user'
  });
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: emptyAddress,
    isActive: true,
    notes: '',
    billingInfo: emptyBillingInfo,
    zohoCustomerId: '',
    zohoBillingAddress: emptyBillingAddress,
    billingAddressOverride: emptyBillingAddress,
    receivingPreferences: defaultReceivingPreferences,
    serviceCapabilities: defaultServiceCapabilities
  });

  // Edit User state
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'regular_user'
  });

  // Permissions state
  const [allPermissions, setAllPermissions] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [newUserPermissions, setNewUserPermissions] = useState({});
  const [editUserPermissions, setEditUserPermissions] = useState({});

  // Fetch customer data
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getCustomer(id);
        // Transform API response to match our form structure
        const customer = response?.data || response; // Try both response.data and direct response
        
        if (!customer || typeof customer !== 'object') {
          throw new Error('No customer data received');
        }
        
        setFormData(buildFormDataFromCustomer(customer));
      } catch (err) {
        console.error('Error fetching customer details:', err);
        setError('Failed to load customer details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCustomerDetails();
    } else {
      setError('Customer ID is missing');
      setLoading(false);
    }
  }, [id]);

  // Fetch users associated with this customer
  useEffect(() => {
    const fetchCustomerUsers = async () => {
      if (!id) return;
      
      try {
        setLoadingUsers(true);
        const response = await adminAPI.getUsersByCustomer(id);
        
        if (response && response.data) {
          // Ensure response.data is an array
          const userData = Array.isArray(response.data) ? response.data : [];
          
          // Ensure each user has a valid ID
          const validUsers = userData.map((user, index) => {
            if (!user || typeof user !== 'object') {
              return { id: `invalid-user-${index}`, name: 'Invalid User', email: 'invalid@example.com', status: 'inactive' };
            }
            
            if (!user.id) {
              return { ...user, id: `temp-id-${index}` };
            }
            
            // Ensure user has a status property
            if (!user.status) {
              return { 
                ...user, 
                status: getCustomerUserStatus(user)
              };
            }
            
            return user;
          });
          
          setUsers(validUsers);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchCustomerUsers();
  }, [id]);

  // Fetch permissions data
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const [permissionsResponse, defaultPermissionsResponse] = await Promise.all([
          adminAPI.getAllPermissions(),
          adminAPI.getDefaultPermissions()
        ]);

        setAllPermissions(permissionsResponse.data || []);
        setDefaultPermissions(defaultPermissionsResponse.data || {});
        setNewUserPermissions(defaultPermissionsResponse.data || {});
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, []);

  // Fetch customer requests when requests tab is active
  useEffect(() => {
    const fetchCustomerRequests = async () => {
      if (activeTab !== 'requests' || !id) return;
      
      try {
        setLoadingRequests(true);
        const response = await adminAPI.getRequests({ companyId: id, limit: 50 });
        setCustomerRequests(response.data || []);
      } catch (error) {
        console.error('Error fetching customer requests:', error);
        setCustomerRequests([]);
      } finally {
        setLoadingRequests(false);
      }
    };

    fetchCustomerRequests();
  }, [activeTab, id]);

  // Fetch customer inventory when inventory tab is active
  useEffect(() => {
    const fetchCustomerInventory = async () => {
      if (activeTab !== 'inventory' || !id) return;
      
      try {
        setLoadingInventory(true);
        const response = await adminAPI.getInventory({ companyId: id, limit: 50 });
        setCustomerInventory(response.data || []);
      } catch (error) {
        console.error('Error fetching customer inventory:', error);
        setCustomerInventory([]);
      } finally {
        setLoadingInventory(false);
      }
    };

    fetchCustomerInventory();
  }, [activeTab, id]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name.includes('.')) {
      // Handle nested objects (address and billingInfo)
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const response = await adminAPI.updateCustomer(id, formData);
      const updatedCustomer = response?.company || response?.data?.company || response?.data || null;
      if (updatedCustomer) {
        setFormData(buildFormDataFromCustomer(updatedCustomer));
      }

      // Exit editing mode
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(err.response?.data?.message || 'Failed to update customer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Function to handle adding a new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      const userData = {
        ...newUserForm,
        permissions: newUserPermissions
      };
      
      const response = await adminAPI.createCustomerUser(id, userData);
      
      let newUser;
      if (response && response.data) {
        newUser = response.data;
      } else if (response && response.user) {
        newUser = response.user;
      } else if (response) {
        // If the response itself is the user data
        newUser = response;
      }
      
      if (newUser && newUser.id) {
        // Ensure the new user has an ID
        if (!newUser.id) {
          newUser.id = `temp-id-${Date.now()}`;
        }
        
        // Add the new user to the list
        setUsers([...users, { ...newUser, status: getCustomerUserStatus(newUser) }]);
        
        // Reset the form and permissions
        setNewUserForm({
          name: '',
          email: '',
          password: '',
          role: 'regular_user'
        });
        setNewUserPermissions(defaultPermissions);
        
        setShowNewUserForm(false);
        setSuccess('User created successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.error('Invalid response format from user creation:', response);
        setError('User was created but the response format was unexpected. Please refresh the page.');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user. Please try again.');
    }
  };

  // Function to handle toggling user status (activate/deactivate)
  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!userId) {
      console.error('Cannot toggle status: User ID is missing');
      setError('Cannot toggle user status: User ID is missing');
      return;
    }

    const isActive = users.find((user) => user.id === userId)?.is_active !== false;
    
    try {
      let response;
      
      if (isActive) {
        response = await adminAPI.deactivateCustomerUser(id, userId);
      } else {
        response = await adminAPI.activateCustomerUser(id, userId);
      }
      
      // Update the local state
      setUsers(users.map(user => {
        if (user.id === userId) {
          const nextIsActive = !isActive;
          const updatedUser = {
            ...user,
            is_active: nextIsActive
          };
          return {
            ...updatedUser,
            status: getCustomerUserStatus(updatedUser)
          };
        }
        return user;
      }));
      
      // Show success message
      setSuccess(`User ${isActive ? 'deactivated' : 'activated'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(`Failed to ${isActive ? 'deactivate' : 'activate'} user. Please try again.`);
    }
  };

  // Handle new user form input changes
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm({
      ...newUserForm,
      [name]: value
    });
  };

  // Function to handle toggling status (activate/deactivate)
  const handleToggleStatus = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (formData.isActive) {
        await adminAPI.deactivateCustomer(id);
        setSuccess('Customer deactivated successfully');
      } else {
        await adminAPI.updateCustomer(id, { ...formData, isActive: true });
        setSuccess('Customer activated successfully');
      }
      
      setFormData({
        ...formData,
        isActive: !formData.isActive
      });
    } catch (err) {
      console.error('Error toggling customer status:', err);
      setError(`Failed to ${formData.isActive ? 'deactivate' : 'activate'} customer. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  // Function to handle deleting customer
  const handleDeleteCustomer = async () => {
    try {
      setDeleteLoading(true);
      setError('');
      setSuccess('');
      
      const response = await adminAPI.deleteCustomer(id);
      
      // Success - navigate back to customers list
      navigate('/admin/customers', { 
        state: { 
          message: 'Customer deleted successfully',
          type: 'success'
        }
      });
    } catch (err) {
      console.error('Error deleting customer:', err);
      
      // Handle specific error messages from backend
      const errorMessage = err.response?.data?.message || 'Failed to delete customer';
      const canDelete = err.response?.data?.canDelete;
      const transactionCount = err.response?.data?.transactionCount;
      const details = err.response?.data?.details;
      
      if (canDelete === false) {
        if (transactionCount > 0) {
          setError("Can't delete customers with transactions!");
        } else {
          setError(errorMessage);
        }
      } else {
        setError(errorMessage);
      }
      
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Function to handle editing a user
  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser || !editingUser.id) {
      console.error('Cannot edit: User ID is missing');
      setError('Cannot edit user: User ID is missing');
      return;
    }
    
    try {
      // Update basic user info
      const userData = {
        ...editUserForm,
        permissions: editUserPermissions
      };
      
      // Remove empty password if not being updated
      if (!userData.password) {
        delete userData.password;
      }
      
      const response = await adminAPI.updateCustomerUser(id, editingUser.id, userData);
      
      let updatedUser;
      if (response && response.data) {
        updatedUser = response.data;
      } else {
        setError('Unexpected response format from the server.');
        return;
      }
      
      if (updatedUser) {
        // Ensure ID is present
        if (!updatedUser.id) {
          updatedUser.id = editingUser.id;
        }
        
        // Update the user in the list
        setUsers(users.map(user => (
          user.id === editingUser.id
            ? {
              ...user,
              ...updatedUser,
              status: getCustomerUserStatus({ ...user, ...updatedUser })
            }
            : user
        )));
        
        // Reset the form
        setEditUserForm({
          name: '',
          email: '',
          password: '',
          role: 'regular_user'
        });
        setEditUserPermissions({});
        
        setShowEditUserForm(false);
        setEditingUser(null);
        
        // Show success message
        setSuccess('User updated successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.error('Invalid response format from user update:', response);
        setError('User was updated but the response format was unexpected. Please refresh the page.');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    }
  };

  // Function to load user permissions when editing
  const handleEditUserClick = async (user) => {
    if (user.is_primary) {
      setError('The primary customer admin cannot be modified from this screen.');
      return;
    }

    setEditingUser(user);
    setEditUserForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'regular_user',
      password: '' // Don't pre-fill password for security
    });

    // Load user's current permissions
    try {
      const permissionsResponse = await adminAPI.getUserPermissions(user.id);
      setEditUserPermissions(permissionsResponse.data || {});
    } catch (error) {
      console.error('Error loading user permissions:', error);
      setEditUserPermissions(defaultPermissions);
    }

    setShowEditUserForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditing 
            ? `Edit Customer - ${formData.companyName || 'Loading...'}`
            : `Customer Details - ${formData.companyName || 'Loading...'}`
          }
        </h1>
        <div className="flex space-x-2">
          {!isEditing ? (
            <>
              <PermissionButton
                permissions="edit_customers"
                onClick={() => handleToggleStatus()}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  formData.isActive 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                hideIfDenied={false}
                disabled={saving}
              >
                {formData.isActive ? (
                  <>
                    <FiToggleLeft className="mr-1" /> {saving ? 'Deactivating...' : 'Deactivate'}
                  </>
                ) : (
                  <>
                    <FiToggleRight className="mr-1" /> {saving ? 'Activating...' : 'Activate'}
                  </>
                )}
              </PermissionButton>
              
              {/* Delete button - only show for inactive customers with admin permissions */}
              {!formData.isActive && canDeleteCustomer && (
                <PermissionButton
                  permissions="delete_customers"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                  hideIfDenied={true}
                  disabled={deleteLoading}
                >
                  <FiX className="mr-1" /> {deleteLoading ? 'Deleting...' : 'Delete Customer'}
                </PermissionButton>
              )}
              
              <PermissionButton
                permissions="edit_customers"
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                hideIfDenied={false}
              >
                <FiEdit className="mr-1" /> Edit
              </PermissionButton>
              <button
                onClick={() => navigate('/admin/customers')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to List
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Company Details
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Requests
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'inventory'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Inventory
          </button>
          <button
            onClick={() => setActiveTab('shipping')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'shipping'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Source Addresses
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('fees')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'fees'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fees
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'contracts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Contracts
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'timeline'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Timeline
          </button>
        </nav>
      </div>

      {activeTab === 'details' && (<>
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
        {/* Company Information */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                disabled={!isEditing}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                Primary Contact Name *
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                disabled={!isEditing}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Company Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isEditing}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Company Phone
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Company Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="address.street" className="block text-sm font-medium text-gray-700">
                Street
              </label>
              <input
                type="text"
                id="address.street"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="address.city" className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="address.state" className="block text-sm font-medium text-gray-700">
                State/Province
              </label>
              <input
                type="text"
                id="address.state"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="address.postalCode" className="block text-sm font-medium text-gray-700">
                Postal Code
              </label>
              <input
                type="text"
                id="address.postalCode"
                name="address.postalCode"
                value={formData.address.postalCode}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="address.country" className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                type="text"
                id="address.country"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Billing Contact / ERP Link */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Billing Contact / ERP Link</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="billingInfo.billingContactName" className="block text-sm font-medium text-gray-700">
                Billing Contact Name
              </label>
              <input
                type="text"
                id="billingInfo.billingContactName"
                name="billingInfo.billingContactName"
                value={formData.billingInfo.billingContactName}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="billingInfo.billingContactEmail" className="block text-sm font-medium text-gray-700">
                Billing Contact Email
              </label>
              <input
                type="email"
                id="billingInfo.billingContactEmail"
                name="billingInfo.billingContactEmail"
                value={formData.billingInfo.billingContactEmail}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="billingInfo.billingContactPhone" className="block text-sm font-medium text-gray-700">
                Billing Contact Phone
              </label>
              <input
                type="text"
                id="billingInfo.billingContactPhone"
                name="billingInfo.billingContactPhone"
                value={formData.billingInfo.billingContactPhone}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label htmlFor="zohoCustomerId" className="block text-sm font-medium text-gray-700">
                Zoho Customer ID
              </label>
              <input
                type="text"
                id="zohoCustomerId"
                name="zohoCustomerId"
                value={formData.zohoCustomerId}
                onChange={handleChange}
                disabled={!isEditing || Boolean(formData.zohoCustomerId)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                ERP-owned. Once set, this link is locked. Billing address source of truth remains Zoho.
              </p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Zoho Billing Address</h3>
              <p className="mt-1 text-xs text-gray-500">
                Read-only ERP address cached from Zoho. This is refreshed when the Zoho customer link is first set or manually synced.
              </p>
              {formData.zohoBillingAddress.street || formData.zohoBillingAddress.city || formData.zohoBillingAddress.country ? (
                <div className="mt-3 text-sm text-gray-700 space-y-1">
                  {formData.zohoBillingAddress.attention && <div>{formData.zohoBillingAddress.attention}</div>}
                  {formData.zohoBillingAddress.street && <div>{formData.zohoBillingAddress.street}</div>}
                  {formData.zohoBillingAddress.addressLine2 && <div>{formData.zohoBillingAddress.addressLine2}</div>}
                  <div>
                    {[formData.zohoBillingAddress.city, formData.zohoBillingAddress.state, formData.zohoBillingAddress.postalCode].filter(Boolean).join(', ')}
                  </div>
                  {formData.zohoBillingAddress.country && <div>{formData.zohoBillingAddress.country}</div>}
                  {formData.zohoBillingAddress.addressId && (
                    <div className="text-xs text-gray-500 pt-2">Zoho Address ID: {formData.zohoBillingAddress.addressId}</div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-gray-500">
                  No Zoho billing address is cached yet.
                </p>
              )}
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Invoice Billing Address Override</h3>
              <p className="mt-1 text-xs text-gray-600">
                Optional. If set, invoice sync will create or reuse this address in Zoho and bill against it instead of the default ERP billing address.
                {!canMutateBilling && ' Read-only: changing it requires billing privileges.'}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <input
                  type="text"
                  name="billingAddressOverride.attention"
                  value={formData.billingAddressOverride.attention}
                  onChange={handleChange}
                  disabled={!isEditing || !canMutateBilling}
                  placeholder="Attention"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
                <input
                  type="text"
                  name="billingAddressOverride.street"
                  value={formData.billingAddressOverride.street}
                  onChange={handleChange}
                  disabled={!isEditing || !canMutateBilling}
                  placeholder="Street address"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
                <input
                  type="text"
                  name="billingAddressOverride.addressLine2"
                  value={formData.billingAddressOverride.addressLine2}
                  onChange={handleChange}
                  disabled={!isEditing || !canMutateBilling}
                  placeholder="Address line 2"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="billingAddressOverride.city"
                    value={formData.billingAddressOverride.city}
                    onChange={handleChange}
                    disabled={!isEditing || !canMutateBilling}
                    placeholder="City"
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <input
                    type="text"
                    name="billingAddressOverride.state"
                    value={formData.billingAddressOverride.state}
                    onChange={handleChange}
                    disabled={!isEditing || !canMutateBilling}
                    placeholder="State / Province"
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <input
                    type="text"
                    name="billingAddressOverride.postalCode"
                    value={formData.billingAddressOverride.postalCode}
                    onChange={handleChange}
                    disabled={!isEditing || !canMutateBilling}
                    placeholder="Postal code"
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <input
                    type="text"
                    name="billingAddressOverride.country"
                    value={formData.billingAddressOverride.country}
                    onChange={handleChange}
                    disabled={!isEditing || !canMutateBilling}
                    placeholder="Country"
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Default Receiving Instructions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['require_serial_numbers_on_receive', 'Require serial numbers at receiving'],
              ['require_mac_addresses_on_receive', 'Require MAC addresses at receiving'],
              ['relabel_on_receive', 'Default to relabel on receipt']
            ].map(([key, label]) => (
              <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!formData.receivingPreferences[key]}
                  disabled={!isEditing}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    receivingPreferences: {
                      ...prev.receivingPreferences,
                      [key]: e.target.checked
                    }
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            These defaults prefill new inbound requests. The actual warehouse instructions are stored on each ASN or manifest.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Billing for serial capture, MAC capture, and relabel still comes from the destination warehouse fee schedule below.
          </p>
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Enabled Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {serviceCapabilityOptions.map(([key, label]) => (
              <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!formData.serviceCapabilities[key]}
                  disabled={!isEditing}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    serviceCapabilities: {
                      ...prev.serviceCapabilities,
                      [key]: e.target.checked
                    }
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Disable services this customer is not contracted to use.
          </p>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!formData.serviceCapabilities.require_request_approval}
                disabled={!isEditing}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  serviceCapabilities: {
                    ...prev.serviceCapabilities,
                    require_request_approval: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <span>Require approval for outbound requests</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              When enabled, customer-created ship, pick &amp; pack, and kit build requests
              require admin approval before work begins. When off, they are auto-approved.
            </p>
          </div>
        </div>

        {/* Notes & Status */}
        <div className="mb-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              ></textarea>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={!isEditing}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active Customer
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions - only show when editing */}
        {isEditing && (
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

      </form>

        {/* Destination Warehouse Access Section - Outside the company
            details form so its own nested <form> (grant access modal)
            submits independently without triggering the parent save. */}
        <CustomerFeesManager customerId={id} showWarehouseAccessOnly={true} />
      </>)}

      {activeTab === 'users' && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Users</h2>
            <PermissionButton
              permissions="create_users"
              onClick={() => setShowNewUserForm(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              hideIfDenied={false}
            >
              <FiUserPlus className="mr-2" /> Add User
            </PermissionButton>
          </div>

          {showNewUserForm && (
            <div className="bg-gray-50 p-4 mb-6 rounded-md">
              <h3 className="text-md font-medium text-gray-900 mb-4">Add New User</h3>
              <form onSubmit={handleAddUser}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newUserForm.name}
                      onChange={handleNewUserChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={newUserForm.email}
                      onChange={handleNewUserChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={newUserForm.password}
                      onChange={handleNewUserChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={newUserForm.role}
                      onChange={handleNewUserChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="regular_user">Regular User</option>
                      <option value="customer_admin">Customer Admin</option>
                    </select>
                  </div>
                </div>
                
                {/* Permissions Section */}
                <div className="mb-4">
                  <PermissionCheckboxList
                    permissions={allPermissions}
                    userPermissions={newUserPermissions}
                    onChange={setNewUserPermissions}
                    showDescriptions={true}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowNewUserForm(false)}
                    className="mr-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </div>
          )}

          {loadingUsers ? (
            <div className="py-4 text-center text-gray-500">
              Loading users...
            </div>
          ) : users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user, index) => (
                    <tr key={user.id || `user-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || 'No name provided'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email || 'No email provided'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          {user.is_primary && (
                            <FiStar className="mr-2 h-4 w-4 text-yellow-500" />
                          )}
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${CUSTOMER_USER_STATUS_BADGES[user.status] || CUSTOMER_USER_STATUS_BADGES.inactive}`}
                          >
                            {CUSTOMER_USER_STATUS_LABELS[user.status] || user.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {!user.is_primary && (
                          <button
                            onClick={() => handleEditUserClick(user)}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                            disabled={!user.id}
                          >
                            <FiEdit className="mr-1 h-4 w-4" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-500">
              No users found for this customer. Click "Add User" to create one.
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserForm && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Edit User
                    </h3>
                    <div className="mt-2">
                      <form onSubmit={handleEditUser} className="space-y-6">
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            value={editUserForm.name}
                            onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            required
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="edit-email"
                            value={editUserForm.email}
                            onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            required
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700">
                            Password (leave blank to keep current password)
                          </label>
                          <input
                            type="password"
                            name="password"
                            id="edit-password"
                            value={editUserForm.password}
                            onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">
                            Role
                          </label>
                          <select
                            id="edit-role"
                            name="role"
                            value={editUserForm.role}
                            onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            required
                          >
                            <option value="regular_user">Regular User</option>
                            <option value="customer_admin">Customer Admin</option>
                          </select>
                        </div>
                        
                        {/* Permissions Section */}
                        <div>
                          <PermissionCheckboxList
                            permissions={allPermissions}
                            userPermissions={editUserPermissions}
                            onChange={setEditUserPermissions}
                            showDescriptions={true}
                          />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  onClick={handleEditUser}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save Changes
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    handleToggleUserStatus(editingUser.id, editingUser.status);
                    setShowEditUserForm(false);
                    setEditingUser(null);
                  }}
                  className={`mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm ${
                    editingUser?.is_active !== false
                      ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
                      : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                  }`}
                >
                  {editingUser?.is_active !== false ? (
                    <>
                      <FiX className="mr-2 h-4 w-4" />
                      Deactivate User
                    </>
                  ) : (
                    <>
                      <FiCheck className="mr-2 h-4 w-4" />
                      Activate User
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEditUserForm(false);
                    setEditUserForm({
                      name: '',
                      email: '',
                      password: '',
                      role: 'regular_user'
                    });
                    setEditUserPermissions({});
                    setEditingUser(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Customer Requests</h2>
            <Link
              to={`/admin/requests/create?companyId=${id}`}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FiClipboard className="mr-2 h-4 w-4" />
              Create Request
            </Link>
          </div>

          {loadingRequests ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading requests...</span>
            </div>
          ) : customerRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Availability
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scheduled
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {request.requestNumber || request.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.requestType === 'incoming' ? 'bg-blue-100 text-blue-800' :
                          request.requestType === 'ship' ? 'bg-green-100 text-green-800' :
                          request.requestType === 'crossdock' ? 'bg-purple-100 text-purple-800' :
                          request.requestType === 'kit_build' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {request.requestType === 'incoming' && <FiTruck className="mr-1 h-3 w-3" />}
                          {request.requestType === 'ship' && <FiPackage className="mr-1 h-3 w-3" />}
                          {request.requestType === 'crossdock' && <FiActivity className="mr-1 h-3 w-3" />}
                          {request.requestType === 'kit_build' && <FiClipboard className="mr-1 h-3 w-3" />}
                          {request.requestType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.status === 'completed' ? 'bg-green-100 text-green-800' :
                          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {formatStatus(request.status, request.requestType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.scheduledDate ? (
                          <span className="flex items-center">
                            <FiCalendar className="mr-1 h-3 w-3" />
                            {format(new Date(request.scheduledDate), 'MMM d, yyyy')}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/admin/requests/${request.id}`}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <FiEye className="mr-1 h-3 w-3" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FiClipboard className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
              <p className="mt-1 text-sm text-gray-500">
                This customer hasn't created any requests yet.
              </p>
              <div className="mt-6">
                <Link
                  to={`/admin/requests/create?companyId=${id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <FiClipboard className="mr-2 h-4 w-4" />
                  Create First Request
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Customer Inventory</h2>
            <Link
              to={`/admin/inventory?companyId=${id}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FiPackage className="mr-2 h-4 w-4" />
              View All Inventory
            </Link>
          </div>

          {loadingInventory ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading inventory...</span>
            </div>
          ) : customerInventory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerInventory.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.sku}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.productName || item.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          Number(item.quantity || 0) <= 0
                            ? 'bg-red-100 text-red-800'
                            : (item.reorderThreshold && Number(item.quantity || 0) <= Number(item.reorderThreshold))
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {Number(item.quantity || 0) <= 0
                            ? 'No Available Stock'
                            : (item.reorderThreshold && Number(item.quantity || 0) <= Number(item.reorderThreshold))
                              ? 'Low Available'
                              : 'Available'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.location || item.warehouseLocation || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.updatedAt ? format(new Date(item.updatedAt), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/admin/inventory/${item.id}`}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <FiEye className="mr-1 h-3 w-3" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FiPackage className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory found</h3>
              <p className="mt-1 text-sm text-gray-500">
                This customer doesn't have any inventory items yet.
              </p>
              <div className="mt-6">
                <Link
                  to={`/admin/requests/create?type=incoming&companyId=${id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <FiTruck className="mr-2 h-4 w-4" />
                  Create Incoming Request
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fees Tab */}
      {activeTab === 'fees' && (
        <>
          {/* Flat-rate storage agreement override (optional) */}
          <StorageAgreementPanel customerId={id} />
          <CustomerFeesManager customerId={id} showFeesOnly={true} />
        </>
      )}

      {/* Source Addresses Tab */}
      {activeTab === 'shipping' && (
        <CompanyWarehousesTab customerId={id} />
      )}

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ContractsTab companyId={id} />
          </div>
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ActivityTimeline
              fetchFn={(params) => adminAPI.getEntityTimeline('Company', id, params)}
              entityType="Company"
              entityId={id}
              limit={25}
              title={null}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiX className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Delete Customer
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to permanently delete customer "{formData.companyName}"? 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteCustomer}
                  disabled={deleteLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Customer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail; 
